import { useCallback, useEffect, useState } from 'react'

import { fetchQuizDiagnosticsChapter } from 'services/QuizDiagnosticsService'

import {
  getSharedSrqStatus,
  patchSharedSrqStatus
} from '../../utils/srqStatusStore'
import { ChapterViewDiagnostics } from '../types'
import { ChapterViewApiResponse } from '../types/api'
import { getChapterApiParams } from '../utils/getChapterApiParams'
import { mapChapterApiResponse } from '../utils/mapChapterApiResponse'

const EMPTY_CHAPTER_VIEW: ChapterViewDiagnostics = {
  srqEnabled: false,
  srqRedDotsVisible: true,
  bookAverage: 0,
  bookTitle: '',
  tag: '',
  tagColour: 'grey',
  studentBookId: '',
  chapterId: '',
  chapterTitle: '',
  chapterOrder: 0,
  chapterAverage: 0,
  chapterSrqRedDotsVisible: true,
  totalQuizzesTaken: 0,
  sections: []
}

export const useQuizDiagnosticsChapterViewData = (chapterId: string) => {
  const [chapterData, setChapterData] =
    useState<ChapterViewDiagnostics>(EMPTY_CHAPTER_VIEW)
  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const handleLoadChapterDiagnostics = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!chapterId) {
        setChapterData(EMPTY_CHAPTER_VIEW)
        setIsLoading(false)
        setHasError(false)
        return
      }

      if (!silent) {
        setIsLoading(true)
      }

      setHasError(false)

      try {
        const response = await fetchQuizDiagnosticsChapter(
          chapterId,
          getChapterApiParams()
        )
        const responseData = response?.data as ChapterViewApiResponse | undefined
        const mapped = mapChapterApiResponse(responseData)

        if (
          mapped.srqEnabled &&
          mapped.srqRedDotsVisible !== getSharedSrqStatus().srqRedDotsVisible
        ) {
          patchSharedSrqStatus({
            srqRedDotsVisible: mapped.srqRedDotsVisible
          })
        }

        setChapterData(mapped)
      } catch (error) {
        console.error('Quiz diagnostics chapter API unavailable:', error)
        setChapterData(EMPTY_CHAPTER_VIEW)
        setHasError(true)
      } finally {
        if (!silent) {
          setIsLoading(false)
        }
      }
    },
    [chapterId]
  )

  useEffect(() => {
    handleLoadChapterDiagnostics()
  }, [handleLoadChapterDiagnostics])

  const refetch = useCallback(() => {
    handleLoadChapterDiagnostics({ silent: true })
  }, [handleLoadChapterDiagnostics])

  const setChapterSrqRedDotsVisible = useCallback((visible: boolean) => {
    setChapterData(previous => ({
      ...previous,
      chapterSrqRedDotsVisible: visible
    }))
  }, [])

  return {
    chapterData,
    isLoading,
    hasError,
    refetch,
    setChapterSrqRedDotsVisible
  }
}
